import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { globalSearchService } from "../../api/search.service";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { MIN_QUERY_LENGTH, SEARCH_DEBOUNCE_MS } from "./searchSections";

/**
 * Debounces the raw input and runs the staff-wide search once it is long enough.
 * React Query aborts the in-flight request (via `signal`) when the key changes,
 * and `keepPreviousData` keeps the last results on screen while the next load.
 */
export function useGlobalSearch(rawQuery: string) {
  const trimmed = rawQuery.trim();
  const debouncedQuery = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
  const enabled = debouncedQuery.length >= MIN_QUERY_LENGTH;

  const query = useQuery({
    queryKey: ["global-search", debouncedQuery],
    queryFn: ({ signal }) => globalSearchService.search(debouncedQuery, signal),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  return {
    ...query,
    debouncedQuery,
    /** True while the user is still typing or a request is in flight. */
    isSearching:
      trimmed.length >= MIN_QUERY_LENGTH && (trimmed !== debouncedQuery || query.isFetching),
  };
}
